/**
 * Reading a frame proposal: what it CHANGES, not what it contains.
 *
 * A persona's axes now fold into the map rather than replacing it, so a
 * proposal carries the whole merged set — every axis the room already had,
 * plus whatever it is asking for. Listing that set says nothing: the reader
 * has to diff two lists of eight by eye to find the one new word. So the
 * strip says what moved.
 *
 * Pure, in its own module, because the component it serves pulls in the room.
 */
import type { BoardroomFrame, BoardroomFrameSpec } from '../../../types';

export type Axis = { dimension: string; meaning: string };

/** "CUISINE: the kind of food" per line, as the hand editor takes it. */
export function parseAxes(text: string): Axis[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const i = l.indexOf(':');
      return i < 0
        ? { dimension: l, meaning: '' }
        : { dimension: l.slice(0, i).trim(), meaning: l.slice(i + 1).trim() };
    });
}

export function axesText(axes: Axis[]): string {
  return axes.map((a) => `${a.dimension}: ${a.meaning}`).join('\n');
}

/** An axis as a person reads it: "price range", not "PRICE_RANGE". */
export const axisLabel = (dimension: string): string =>
  dimension.toLowerCase().replace(/_/g, ' ');

export interface AxisDiff {
  added: string[];
  removed: string[];
  sharpened: string[];
}

export function axisDiff(current: Axis[] | undefined, next: Axis[] | undefined): AxisDiff {
  const before = new Map((current ?? []).map((a) => [a.dimension, a.meaning ?? '']));
  const after = new Map((next ?? []).map((a) => [a.dimension, a.meaning ?? '']));
  const added: string[] = [];
  const sharpened: string[] = [];
  after.forEach((meaning, dim) => {
    if (!before.has(dim)) added.push(dim);
    else if (meaning !== before.get(dim)) sharpened.push(dim);
  });
  const removed: string[] = [];
  before.forEach((_m, dim) => {
    if (!after.has(dim)) removed.push(dim);
  });
  return { added, removed, sharpened };
}

/** One line on what a proposal does to the map. Empty when it changes no axis
 * — the vocabulary may still have moved, which the strip shows separately. */
export function proposalSummary(
  current: BoardroomFrame | null,
  proposal: Pick<BoardroomFrame, 'axes'>,
): string {
  const d = axisDiff(current?.axes, proposal.axes);
  const parts: string[] = [];
  if (d.added.length) parts.push(`adds ${d.added.map(axisLabel).join(', ')}`);
  if (d.removed.length) parts.push(`removes ${d.removed.map(axisLabel).join(', ')}`);
  if (d.sharpened.length) parts.push(`sharpens ${d.sharpened.map(axisLabel).join(', ')}`);
  return parts.join(' · ');
}

// ----------------------------- the spec sheet -----------------------------
//
// The other half of a frame, and the same reading problem: the sheet MERGES
// too, so a proposal that adds a dozen columns arrives carrying every column
// the room already had. What matters is which ones are new.

export interface SpecDiff {
  added: string[];
  removed: string[];
  sharpened: string[];
  /** `[old, new]`. A column keeping its values under a better name. */
  renamed: [string, string][];
}

export function specDiff(
  current: BoardroomFrameSpec[] | undefined,
  next: BoardroomFrameSpec[] | undefined,
): SpecDiff {
  const key = (s: BoardroomFrameSpec) => `${s.kind}${s.unit ? ` in ${s.unit}` : ''} ${s.meaning ?? ''}`;
  const before = new Map((current ?? []).map((s) => [s.attribute, key(s)]));
  const after = new Map((next ?? []).map((s) => [s.attribute, key(s)]));
  /* A RENAME IS NOT AN ADD PLUS A REMOVE. Read as two it says "we are losing
     `api access` and gaining `api`" — and losing a column takes its values
     with it, so that is the one shape a person would refuse. What is actually
     on offer is the same column under a name the market recognises, every
     recorded value carried across. */
  const renamed: [string, string][] = [];
  (next ?? []).forEach((s) =>
    (s.replaces ?? []).forEach((old) => {
      if (before.has(old) && old !== s.attribute) renamed.push([old, s.attribute]);
    }),
  );
  const wasRenamed = new Set(renamed.map(([o]) => o));
  const isRenamed = new Set(renamed.map(([, n]) => n));
  const added: string[] = [];
  const sharpened: string[] = [];
  after.forEach((k, attr) => {
    if (isRenamed.has(attr)) return;
    if (!before.has(attr)) added.push(attr);
    else if (k !== before.get(attr)) sharpened.push(attr);
  });
  const removed: string[] = [];
  before.forEach((_k, attr) => {
    if (!after.has(attr) && !wasRenamed.has(attr)) removed.push(attr);
  });
  return { added, removed, sharpened, renamed };
}

/** Whether a proposal lands exactly what is already standing.
 *
 *  A pass that reads four hundred phrases and arrives at the columns the room
 *  already had is a good outcome, not a decision — so it must not be dressed
 *  as one. Mirrors `frame.same_frame` on the backend, and is deliberately
 *  STRICTER than `specDiff`/`axisDiff`, which compare names only: an axis
 *  whose meaning was rewritten is a real change those two cannot see. */
export function sameFrame(
  current: BoardroomFrame | null,
  proposal: Pick<BoardroomFrame, 'axes' | 'vocab' | 'specs'>,
): boolean {
  if (!current) return false;
  const axes = (f: Pick<BoardroomFrame, 'axes'>) =>
    JSON.stringify((f.axes ?? []).map((a) => [a.dimension, a.meaning ?? '']));
  const cols = (f: Pick<BoardroomFrame, 'specs'>) =>
    JSON.stringify((f.specs ?? []).map((c) => [c.attribute, c.kind, c.unit, c.meaning ?? '', c.replaces ?? []]));
  /* KEY ORDER IS NOT A CHANGE. `JSON.stringify` on an object is
     order-sensitive, so two identical vocabularies serialised by different
     code paths would compare unequal — harmless here (it would show a real
     card for a no-op) but the opposite mistake is not, so both halves are
     made deterministic rather than relying on one of them being safe. */
  const words = (f: Pick<BoardroomFrame, 'vocab'>) =>
    JSON.stringify(
      Object.entries((f.vocab ?? {}) as Record<string, string>)
        .filter(([, v]) => (v ?? '') !== '')
        .sort(([a], [b]) => a.localeCompare(b)),
    );
  return (
    words(current) === words(proposal) &&
    axes(current) === axes(proposal) &&
    cols(current) === cols(proposal)
  );
}

/** What a proposal does to the sheet, in one line.
 *
 *  Counted rather than listed when it is the whole-set pass: "adds 12 columns"
 *  is the shape of the thing, and twelve names in a summary line is not a
 *  summary. The names are in the card below it. */
export function sheetSummary(
  current: BoardroomFrame | null,
  proposal: Pick<BoardroomFrame, 'specs'>,
): string {
  const d = specDiff(current?.specs, proposal.specs);
  const parts: string[] = [];
  if (d.added.length) {
    parts.push(
      d.added.length > 3
        ? `adds ${d.added.length} columns`
        : `adds ${d.added.join(', ')}`,
    );
  }
  if (d.renamed.length) {
    parts.push(
      d.renamed.length > 2
        ? `renames ${d.renamed.length} columns`
        : d.renamed.map(([o, n]) => `renames ${o} → ${n}`).join(', '),
    );
  }
  if (d.removed.length) parts.push(`removes ${d.removed.join(', ')}`);
  if (d.sharpened.length) parts.push(`sharpens ${d.sharpened.join(', ')}`);
  return parts.join(' · ');
}

/** Everything a proposal changes — the map and the sheet — in one line. */
export function changeSummary(
  current: BoardroomFrame | null,
  proposal: Pick<BoardroomFrame, 'axes' | 'specs'>,
): string {
  return [proposalSummary(current, proposal), sheetSummary(current, proposal)]
    .filter(Boolean)
    .join(' · ');
}

/** A proposal that would change nothing — a check that came back "it holds
 *  up". Both tests must agree, so a real change always keeps its Accept. */
export function isNoOpProposal(current: BoardroomFrame | null, proposal: BoardroomFrame): boolean {
  return sameFrame(current, proposal) && !changeSummary(current, proposal);
}

/** How many frame proposals are waiting on a yes or a no. */
export function framesAwaiting(current: BoardroomFrame | null, proposals: BoardroomFrame[]): number {
  return proposals.filter(p => !isNoOpProposal(current, p)).length;
}
